
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsEsignuser from 'Models/Security/Acl/VisitorsEsignuser';
import UserEsignuser from 'Models/Security/Acl/UserEsignuser';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IEsignuserEntityAttributes extends IModelAttributes {
	name: string;
	phone: string;
	email: string;
	signature: string;
	status: Enums.esignuserstatus;

	esignceremonysigneess: Array<
		| Models.EsignceremonysigneeEntity
		| Models.IEsignceremonysigneeEntityAttributes
	>;
	esignceremonywitnessss: Array<
		| Models.EsignceremonywitnessEntity
		| Models.IEsignceremonywitnessEntityAttributes
	>;

}

@entity('EsignuserEntity', 'ESignUser')
export default class EsignuserEntity extends Model
	implements IEsignuserEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsEsignuser(),
		new UserEsignuser(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<EsignuserEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;

	@observable
	@attribute<EsignuserEntity, string>()
	@CRUD({
		name: 'Phone',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public phone: string;

	@observable
	@attribute<EsignuserEntity, string>()
	@CRUD({
		name: 'Email',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public email: string;

	@observable
	@attribute<EsignuserEntity, string>()
	@CRUD({
		name: 'Signature',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public signature: string;

	@observable
	@attribute<EsignuserEntity, Enums.esignuserstatus>()
	@CRUD({
		name: 'Status',
		displayType: 'enum-combobox',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.esignuserstatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.esignuserstatusOptions),
		displayFunction: (attr: Enums.esignuserstatus) => Enums.esignuserstatusOptions[attr],
	})
	public status: Enums.esignuserstatus;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ESignCeremonySigneess',
		displayType: 'reference-multicombobox',
		order: 60,
		referenceTypeFunc: () => Models.EsignceremonysigneeEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esignceremonysigneess',
			oppositeEntity: () => Models.EsignceremonysigneeEntity,
		}),
		
	})
	public esignceremonysigneess: Models.EsignceremonysigneeEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ESignCeremonyWitnessss',
		displayType: 'reference-multicombobox',
		order: 70,
		referenceTypeFunc: () => Models.EsignceremonywitnessEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esignceremonywitnessss',
			oppositeEntity: () => Models.EsignceremonywitnessEntity,
		}),
		
	})
	public esignceremonywitnessss: Models.EsignceremonywitnessEntity[] = [];



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IEsignuserEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IEsignuserEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.signature !== undefined) {
				this.signature = attributes.signature;
			}
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.esignceremonysigneess !== undefined && Array.isArray(attributes.esignceremonysigneess)) {
				for (const model of attributes.esignceremonysigneess) {
					if (model instanceof Models.EsignceremonysigneeEntity) {
						this.esignceremonysigneess.push(model);
					} else {
						this.esignceremonysigneess.push(new Models.EsignceremonysigneeEntity(model));
					}
				}
			}
			if (attributes.esignceremonywitnessss !== undefined && Array.isArray(attributes.esignceremonywitnessss)) {
				for (const model of attributes.esignceremonywitnessss) {
					if (model instanceof Models.EsignceremonywitnessEntity) {
						this.esignceremonywitnessss.push(model);
					} else {
						this.esignceremonywitnessss.push(new Models.EsignceremonywitnessEntity(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		esignceremonysigneess {
			${Models.EsignceremonysigneeEntity.getAttributes().join('\n')}
		}
		esignceremonywitnessss {
			${Models.EsignceremonywitnessEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			esignceremonysigneess: {},
			esignceremonywitnessss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(EsignuserEntity.prototype, 'created');
CRUD(modifiedAttr)(EsignuserEntity.prototype, 'modified');
